import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { createBreadcrumbWithI18n } from '~/apps/ninesegsAnalyzer/routes/paths'
import { pathKey } from '~/apps/ninesegsAnalyzer/utils/customerAnalysis'
import { HStack } from '~/components/elements/HStack'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import { VStack } from '~/components/elements/VStack'
import { DataDisplayTypeAtomSwitcher } from '~/components/features/DataDisplayTypeAtomSwitcher'
import { SegmentDisplaySwitcher } from '~/components/features/SegmentDisplaySwitcher'
import { SharedBrandGroupSelector } from '~/components/features/SharedBrandGroupSelector'
import { useRestoreOnMount } from '~/hooks/useRestoreOnMount'
import { useScrollEffect } from '~/hooks/useScrollEffect'
import { exists } from '~/utils'

import {
  dataDisplayTypeAtom,
  isTabulationReadyAtom,
  segmentDisplayTypeAtom,
  tabulationQueryDataAtom,
} from '../atoms'
import { useRestoreParams } from '../hooks/restoringParams/useRestoreParams'
import { useSurveyId } from '../hooks/useSurveyId'
import { useTabulationQuery } from '../hooks/useTabulationQuery'

import { CustomerAnalysisTable } from './CustomerAnalysisTable'

export const CustomerAnalysis = () => {
  const surveyId = useSurveyId()
  const { t } = useTranslation()

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('basicAnalysis'),
      createBreadcrumb(pathKey, { surveyId }),
    ],
    [createBreadcrumb, surveyId],
  )

  const isExecuting = useTabulationQuery()

  const restoreParams = useRestoreParams()
  useRestoreOnMount(restoreParams, isTabulationReadyAtom)

  const tabulationQueryData = useAtomValue(tabulationQueryDataAtom)
  useScrollEffect(exists(tabulationQueryData))

  return (
    <PageContainer breadcrumbs={breadcrumbs} executing={isExecuting}>
      <VStack spacing={8} verticalAppearance="stretch-scroll">
        <VStack spacing={4}>
          <LabelingContainer label={t('brand_group')}>
            <SharedBrandGroupSelector />
          </LabelingContainer>

          <LabelingContainer label={t('switch_view')}>
            <HStack spacing={4}>
              <DataDisplayTypeAtomSwitcher
                dataDisplayTypeAtom={dataDisplayTypeAtom}
              />

              <SegmentDisplaySwitcher
                segmentDisplayTypeAtom={segmentDisplayTypeAtom}
                withBoth={true}
              />
            </HStack>
          </LabelingContainer>
        </VStack>

        <CustomerAnalysisTable />
      </VStack>
    </PageContainer>
  )
}
